<script>
import {onMount} from 'svelte';
import {
    authState
} from '@app/store/authStore.svelte.js';
import {
    modalState, hideModal, showModal
} from '@app/store/modalStore.svelte.js';
import { toast } from '@zerodevx/svelte-toast';
import {
    projectState
} from '@app/store/projectStore.svelte.js';
import {
    eventGroupState
} from '@app/store/eventGroupStore.svelte.js';
import {
    fade
} from 'svelte/transition';
import {
    BarLoader
} from 'svelte-loading-spinners';
import * as m from '@app/paraglide/messages.js';
/* Firebase */
import firebase from '@app/configs/firebase.js';
import 'firebase/compat/functions';
import db from '@app/configs/firestore.js';
import {v4 as uuidv4} from 'uuid';
import Swal from 'sweetalert2'
import { SWAL_BASE, SWAL_CLASSES } from "@app/lib/swalClasses.js";

let teamId = authState.team.id;
let dbRef = db.collection("teams").doc(teamId).collection("projects").doc(projectState.teamId).collection("snapshots");
let sharesRef = db.collection("teams").doc(teamId).collection("shares");

onMount(async () => {
    let snapshotRes = await dbRef.get();
    if (snapshotRes.empty){
        /* Alert user using sweetalert that the project does not have any commits. We recommend syncing changes first before sharing the project with team members. */
        Swal.fire({
            ...SWAL_BASE,
            customClass: SWAL_CLASSES.ok,
            title: m.team_share_no_commits_title(),
            text: m.team_share_no_commits_text(),
            icon: 'warning',
            confirmButtonText: m.team_share_no_commits_confirm(),
            allowOutsideClick : false,
            allowEscapeKey : false,
        })
    }
});

/* 
Share Code:
{
    id : uuid,
    name : "display name",
    members : [*member emails*],
    groups : [*event group uuid*],
    access : "editor", //editor, viewer, reviewer
    createdOn: creationDate,
    projectTeamId : *projectTeamId*,
    fullPermissions: boolean
}

*/

let userId = firebase.auth().currentUser.uid, email = firebase.auth().currentUser.email;
let shareCodes = $state([]), 
    selectedShareCode = $state({
        members : [],
        groups : [],
        fullPermissions: false
    }), 
    dbWork = $state();

// Function to handle full permissions toggle
function handleFullPermissions() {
    if (selectedShareCode.fullPermissions) {
        // Auto-select all event groups when full permissions is enabled
        selectedShareCode.groups = eventGroupState.map(evg => evg.id);
    }
}

async function createShareCode(){
    shareCodes = [
        ...shareCodes,
        {
            id : uuidv4(),
            name : m.team_share_untitled_name(),
            members : [],
            groups : [],
            ownerId :  userId,
            ownerEmail : email,
            access : "editor", //editor, viewer, reviewer
            createdOn: firebase.firestore.Timestamp.fromDate(new Date()),
            teamId : authState.team.id,
            projectTeamId : projectState.teamId,
            fullPermissions: false,
            new : true
        }
    ]

    selectedShareCode = shareCodes[shareCodes.length-1];
    toast.push(m.team_share_new_share_created(), {classes: ['toast-success']});
}

async function saveShareCode(){
    try {
        if (!selectedShareCode.name){
            throw new Error(m.team_share_error_name_required());
        }

        if (selectedShareCode.members.length === 0){
            throw new Error(m.team_share_error_members_required());
        }

        if (selectedShareCode.groups.length === 0){
            throw new Error(m.team_share_error_groups_required());
        }
        
        // If full permissions is enabled, ensure all event groups are included
        if (selectedShareCode.fullPermissions) {
            selectedShareCode.groups = eventGroupState.map(evg => evg.id);
        }
        
        selectedShareCode.new = false;
        // Replace function call with direct Firestore write
        await sharesRef.doc(selectedShareCode.id).set(selectedShareCode);
        toast.push(m.team_share_saved_successfully(), {classes: ['toast-success']});
    } catch(err){
        console.log(err,err.message);
        toast.push(m.team_share_save_error({ message: err.message }), {classes: ['toast-danger']});
    } finally {
        return setup();
    }
}

async function removeShareCode(){
    if (!selectedShareCode.id){
        return;
    }

    try { 
        // Replace function call with direct Firestore delete
        await sharesRef.doc(selectedShareCode.id).delete();
        toast.push(m.team_share_removed_successfully(), {classes: ['toast-success']});
    } catch(err){
        console.log(err,err.message);
        toast.push(m.team_share_remove_error({ message: err.message }), {classes: ['toast-danger']});
    } finally {
        return setup();
    }
}

function copyShareCode(){
    if (!selectedShareCode.id){
        toast.push(m.team_share_no_share_selected(), {classes: ['toast-danger']});
        return;
    }

    navigator.clipboard.writeText(`https://app.closedcaptioncreator.com/share?id=${selectedShareCode.id}`);

    toast.push(m.team_share_code_copied(), {classes: ['toast-success']});
}

async function setup(){
    selectedShareCode = {
        groups : [],
        members : [],
        fullPermissions: false
    };
    
    try {
        // Replace function call with direct Firestore query
        const querySnapshot = await sharesRef.where("projectTeamId", "==", projectState.teamId).get();
        shareCodes = [];
        querySnapshot.forEach(doc => {
            shareCodes.push(doc.data());
        });
        selectedShareCode = shareCodes[0] || selectedShareCode;
    } catch(err) {
        console.log(err, err.message);
        toast.push(m.team_share_get_error({ message: err.message }), {classes: ['toast-danger']});
    }
}

if (authState.team && authState.team.members.length > 1){
    dbWork = setup();
}
</script>

<div 
    transition:fade={{duration: 100}} 
    class="modal {modalState.selected === 'teamShare' ? 'show d-block' : ''}" 
    role="dialog"
    aria-labelledby="teamShareTitle"
    aria-modal="true"
    tabindex="-1" 
    id="TeamShare">
    <div class="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable" role="document">
        <div class="modal-content">
            <div class="modal-header">
                <h4 class="modal-title" id="teamShareTitle">{m.team_share_title()}</h4>
                <button type="button" class="btn-close" aria-label={m.close()} onclick={hideModal}></button>
            </div>
            <div class="modal-body">
                <div class="modal-body">
                    {#if authState.team && authState.team.members.length > 1}
                        {#if shareCodes.length === 0}
                            <p class="text-warning fw-bold">{m.team_share_create_new_prompt()}</p>
                        {/if}
                        <div class="row">
                            <div class="col-4">
                                <h4>{m.team_share_shares_label()}<span class="badge rounded-pill bg-secondary d-xl-flex justify-content-xl-end align-items-xl-center float-end fs-6">{m.team_share_links_count({ count: shareCodes.length })}</span></h4>
                                {#await dbWork}
                                    <div class="d-flex justify-content-center align-items-center p-3">
                                        <BarLoader size="200" color="#1eb4b2" unit="px" duration="3s"></BarLoader>
                                    </div>       
                                {:then}
                                <select 
                                    id="shareCodeList"
                                    class="w-100" 
                                    size={Math.min(shareCodes.length + 3, 15)} 
                                    bind:value={selectedShareCode}
                                    aria-label={m.team_share_available_share_codes_label()}>
                                    {#each shareCodes as code (code.id)}
                                        <option value={code}>{code.name}</option>
                                    {/each}
                                </select>
                                <div class="d-xl-flex justify-content-xl-end py-2">
                                    <div class="btn-group" role="group">

                                        <button class="btn btn-primary" type="button" onclick={() => createShareCode()}>
                                            <i class="bi bi-plus-lg"></i> {m.team_share_new_share_button()}
                                        </button>

                                        <button class="btn btn-light" disabled={!selectedShareCode.id} type="button" onclick={() => dbWork = removeShareCode()}>
                                            <i class="bi bi-trash"></i> {m.team_share_delete_share_button()}
                                        </button>
                                    </div>
                                </div>
                                {:catch}
                                    <p class="text-danger">{m.team_share_load_error()}</p>
                                {/await}
                            </div>
                            <div class="col">
                                <div class="bg-secondary border rounded border-secondary shadow p-3">
                                    <form>
                                        <div class="mb-3">
                                            <label class="form-label" for="Share Name">{m.team_share_name_label()}</label>
                                            <input class="form-control" type="text" placeholder={m.team_share_name_placeholder()} disabled={!selectedShareCode.new} bind:value={selectedShareCode.name}>
                                        </div>
                                        <div class="mb-3">
                                            <label class="form-label" for="Team Members">{m.team_share_team_members_label()} <i class="bi bi-info-circle"></i></label>
                                            <button class="btn btn-outline-dark btn-sm m-2" type="button" disabled={!selectedShareCode.new} onclick={() => {selectedShareCode.members = [...authState.team.members]}}>{m.team_share_select_all_button()}</button>
                                            
                                            <select 
                                                id="teamMemberList"
                                                class="form-select" 
                                                multiple 
                                                size={Math.min( authState.team.members.length + 3, 10)} 
                                                bind:value={selectedShareCode.members} 
                                                disabled={!selectedShareCode.new}
                                                aria-label={m.team_share_select_team_members_label()}>
                                                {#each authState.team.members as member (member)}
                                                    <option value={member}>{member}</option>
                                                {/each}
                                            </select>
                                        </div>
                                        
                                        <!-- Full Permissions checkbox -->
                                        <div class="mb-3 form-check">
                                            <input 
                                                type="checkbox" 
                                                class="form-check-input" 
                                                id="fullPermissions" 
                                                bind:checked={selectedShareCode.fullPermissions} 
                                                disabled={!selectedShareCode.new}
                                                onchange={handleFullPermissions}>
                                            <label class="form-check-label" for="fullPermissions">
                                                    {m.team_share_full_permissions_label()}
                                                    <i class="bi bi-info-circle" title={m.team_share_full_permissions_tooltip()}></i>
                                            </label>
                                        </div>
                                        
                                        <!-- Only show Event Groups selection if fullPermissions is not enabled -->
                                        {#if !selectedShareCode.fullPermissions}
                                        <div class="mb-3">
                                            <label class="form-label" for="Event Groups To Share">{m.team_share_event_groups_label()} <i class="bi bi-info-circle"></i></label>
                                            <button class="btn btn-outline-dark btn-sm m-2" type="button" disabled={!selectedShareCode.new} onclick={() => {selectedShareCode.groups = eventGroupState.map((evg) => {return evg.id})}}>
                                                {m.team_share_select_all_button()}
                                            </button>
                                            <select 
                                                id="eventGroupList"
                                                class="form-select" 
                                                multiple 
                                                size={Math.min( eventGroupState.length + 3, 10)} 
                                                bind:value={selectedShareCode.groups} 
                                                disabled={!selectedShareCode.new}
                                                aria-label={m.team_share_select_event_groups_label()}>
                                                {#each eventGroupState as eventGroup (eventGroup.id)}
                                                    <option value={eventGroup.id}>{eventGroup.name}</option>
                                                {/each}
                                            </select>
                                        </div>
                                        {:else}
                                        <div class="mb-3">
                                            <div class="alert alert-info">
                                                <i class="bi bi-info-circle-fill me-2"></i>
                                                {m.team_share_full_permissions_enabled_message()}
                                            </div>
                                        </div>
                                        {/if}
                                        
                                        {#await dbWork}
                                            <div class="d-flex justify-content-center align-items-center p-3">
                                                <BarLoader size="200" color="#1eb4b2" unit="px" duration="3s"></BarLoader>
                                            </div>       
                                        {:then}
                                        <div class="d-flex justify-content-end">
                                            {#if !selectedShareCode.new}
                                            <div class="input-group mb-3">
                                                <input type="text" class="form-control" placeholder={m.team_share_code_placeholder()} disabled value={selectedShareCode.id || ''}>
                                                <button class="btn btn-info text-white" type="button" disabled={!selectedShareCode.id || selectedShareCode.new} onclick={() => copyShareCode()}><i class="bi bi-link-45deg"></i> {m.team_share_copy_link_button()}
                                                </button>
                                              </div>
                                            {:else}
                                            <div class="btn-group" role="group">
                                                <button class="btn btn-primary" type="button" onclick={() => {dbWork = saveShareCode()}} disabled={!selectedShareCode.new}>{m.team_share_save_button()}</button>
                                                <button class="btn btn-outline-dark" type="button" onclick={() => {dbWork = setup()}} disabled={!selectedShareCode.new}>{m.team_share_cancel_button()}</button>
                                            </div>
                                            {/if}
                                        </div>
                                        {/await}
                                    </form>
                                </div>
                            </div>
                        </div>
                    {:else}
                        <div class="d-flex justify-content-center align-items-center p-3">
                            <div>
                                <p>{m.team_share_create_team_prompt()}</p>
                                <button type="button" class="btn btn-primary w-100" onclick={() => {showModal('teamManager')}}>{m.team_share_manage_team_button()}</button>
                            </div>                        
                        </div>                        
                    {/if}
                </div>
            </div>
        </div>
    </div>
</div>
